'use client';

import { useMemo } from 'react';
import Link from 'next/link';
import { Box, Button, Container, Group, Paper, SimpleGrid, Stack, Text, Title, TypographyStylesProvider } from '@mantine/core';
import { IconArrowRight } from '@tabler/icons-react';
import type { Locale } from '../../i18n/locales';
import type { ContentPageData, SiteSettingsData } from '../../types/content';
import { getCarComparisonLinkProps, resolvePublicHref } from '../../lib/content/comparisonLinks';
import { Header } from '../layout/Header';
import { Footer } from '../layout/Footer';
import { PublicPageShell } from '../layout/PublicPageShell';
import { useRealtimeRefresh } from '../../hooks/useRealtimeRefresh';
import { MantineNextImage } from '../ui/MantineNextImage';

const toPlainText = (value?: string) => (value ? value.replace(/<[^>]*>?/gm, '').replace(/\s+/g, ' ').trim() : '');

export function CompareServicePageView({
  locale,
  siteSettings,
  data,
}: {
  locale: Locale;
  siteSettings: SiteSettingsData;
  data: ContentPageData;
}) {
  const sections = (data.sections || []).filter((section) => section.title || section.body || section.imageUrl);
  const primaryCtaLabel = data.primaryCtaLabel;
  const primaryCtaHref = primaryCtaLabel
    ? resolvePublicHref(locale, data.primaryCtaHref || '/vertailu/autovakuutus/tiedot')
    : undefined;
  const secondaryCtaLabel = data.secondaryCtaLabel;
  const secondaryCtaHref = data.secondaryCtaHref ? resolvePublicHref(locale, data.secondaryCtaHref) : undefined;
  const intro = data.heroSubtitle || toPlainText(data.body);
  const heroImageUrl = data.heroImageUrl || sections[0]?.imageUrl;
  const stepSections = sections.slice(0, 3);
  const detailSections = sections
    .slice(3)
    .filter((section) => section.title?.trim().toLowerCase() !== 'mitä vertailussa huomioidaan?');
  const refreshSubscriptions = useMemo(
    () => [
      { table: 'content_pages', filter: `slug=eq.${data.slug}` },
      { table: 'content_page_sections', filter: `page_slug=eq.${data.slug}` },
    ],
    [data.slug],
  );
  useRealtimeRefresh(refreshSubscriptions);

  return (
    <PublicPageShell>
      <Header locale={locale} settings={siteSettings} />

      <Box py={{ base: 42, md: 66 }}>
        <Container size="xl">
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing={26}>
            <Stack gap={14} justify="center">
              {data.eyebrow ? (
                <Text size="sm" fw={600} style={{ color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {data.eyebrow}
                </Text>
              ) : null}
              <Title
                order={1}
                style={{
                  color: 'var(--text-primary)',
                  fontWeight: 600,
                  lineHeight: 1.15,
                  letterSpacing: '-0.03em',
                  fontSize: 'clamp(2.1rem, 4.4vw, 3.4rem)',
                  maxWidth: 720,
                }}
              >
                {data.title}
              </Title>
              {intro ? (
                <Text style={{ color: 'var(--text-secondary)', maxWidth: 680, lineHeight: 1.75 }}>{intro}</Text>
              ) : null}
              <Group gap="xs">
                {primaryCtaLabel && primaryCtaHref ? (
                  <Button
                    component={Link}
                    href={primaryCtaHref}
                    {...getCarComparisonLinkProps(primaryCtaHref)}
                    rightSection={<IconArrowRight size={16} />}
                    styles={{ root: { background: 'var(--brand-primary)', color: '#fff' } }}
                  >
                    {primaryCtaLabel}
                  </Button>
                ) : null}
                {secondaryCtaLabel && secondaryCtaHref ? (
                  <Button
                    component={Link}
                    href={secondaryCtaHref}
                    {...getCarComparisonLinkProps(secondaryCtaHref)}
                    variant="default"
                    styles={{ root: { background: 'var(--surface-soft)', borderColor: 'var(--surface-border)', color: 'var(--text-primary)' } }}
                  >
                    {secondaryCtaLabel}
                  </Button>
                ) : null}
              </Group>
            </Stack>

            {heroImageUrl ? (
              <Paper radius={32} p={10} withBorder style={{ borderColor: 'var(--surface-border)', background: 'var(--surface-muted)', overflow: 'hidden' }}>
                <MantineNextImage
                  src={heroImageUrl}
                  alt={data.heroImageAlt || data.title}
                  radius={24}
                  h={{ base: 250, md: 400 }}
                  fit="cover"
                />
              </Paper>
            ) : null}
          </SimpleGrid>
        </Container>
      </Box>

      {stepSections.length ? (
        <Container size="xl" pb={{ base: 44, md: 56 }}>
          <SimpleGrid cols={{ base: 1, md: 3 }} spacing={16}>
            {stepSections.map((section, index) => (
              <Paper
                key={`${section.title || 'step'}-${index}`}
                p={20}
                radius={24}
                withBorder
                style={{ borderColor: 'var(--surface-border)', background: index === 1 ? 'var(--surface-soft)' : '#fff' }}
              >
                <Group gap={10} mb={10}>
                  <Box
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: 999,
                      background: 'var(--brand-primary)',
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: 13,
                      display: 'grid',
                      placeItems: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {index + 1}
                  </Box>
                  <Text fw={700} style={{ color: 'var(--text-primary)' }}>
                    {section.title}
                  </Text>
                </Group>
                <TypographyStylesProvider>
                  <div className="content-rich-text" dangerouslySetInnerHTML={{ __html: section.body || '' }} />
                </TypographyStylesProvider>
              </Paper>
            ))}
          </SimpleGrid>
        </Container>
      ) : null}

      {detailSections.length ? (
        <Container size="xl" pb={{ base: 52, md: 72 }}>
          <Stack gap={16}>
            {detailSections.map((section, index) => (
              <Paper
                key={`${section.title || 'detail'}-${index}`}
                p={{ base: 18, md: 24 }}
                radius={26}
                withBorder
                style={{ borderColor: 'var(--surface-border)', background: index % 2 === 0 ? '#fff' : 'var(--surface-soft)' }}
              >
                <SimpleGrid cols={{ base: 1, md: section.imageUrl ? 2 : 1 }} spacing={18}>
                  <Stack gap={10} style={{ order: index % 2 === 0 ? 1 : 2 }}>
                    <Text fw={700} style={{ color: 'var(--text-primary)', fontSize: '1.15rem' }}>
                      {section.title}
                    </Text>
                    <TypographyStylesProvider>
                      <div className="content-rich-text" dangerouslySetInnerHTML={{ __html: section.body || '' }} />
                    </TypographyStylesProvider>
                  </Stack>
                  {section.imageUrl ? (
                    <MantineNextImage
                      src={section.imageUrl}
                      alt={section.imageAlt || section.title || data.title}
                      radius={18}
                      h={210}
                      fit="cover"
                      style={{ order: index % 2 === 0 ? 2 : 1 }}
                    />
                  ) : null}
                </SimpleGrid>
              </Paper>
            ))}
          </Stack>
        </Container>
      ) : null}

      <Footer locale={locale} settings={siteSettings} />
    </PublicPageShell>
  );
}
